export const GENERATION_MODE_LABELS = {
    DOC_GROUNDED: 'Doc-grounded',
    GENERIC_SCOPE_ONLY: 'Baseline lesson',
    PROFILE_INCOMPLETE: 'Profile incomplete',
    PARTIAL_ENRICHMENT_OMITTED: 'Partial enrichment omitted',
    FAILED_AFTER_RETRIES: 'Failed after retries',
    GENERIC_FALLBACK_REJECTED: 'Generic fallback rejected',
};

const MODE_BADGE_MAP = {
    DOC_GROUNDED: {
        label: GENERATION_MODE_LABELS.DOC_GROUNDED,
        tone: 'success',
        className: 'bg-emerald-100 text-emerald-700',
        description: 'Uploaded instructional documents materially shaped this lesson.',
    },
    GENERIC_SCOPE_ONLY: {
        label: GENERATION_MODE_LABELS.GENERIC_SCOPE_ONLY,
        tone: 'neutral',
        className: 'bg-slate-100 text-slate-600',
        description: 'Saige returned a usable baseline lesson without document grounding.',
    },
    PROFILE_INCOMPLETE: {
        label: GENERATION_MODE_LABELS.PROFILE_INCOMPLETE,
        tone: 'warning',
        className: 'bg-amber-100 text-amber-700',
        description: 'Generation could not start until subject and grade were present.',
    },
    PARTIAL_ENRICHMENT_OMITTED: {
        label: GENERATION_MODE_LABELS.PARTIAL_ENRICHMENT_OMITTED,
        tone: 'warning',
        className: 'bg-amber-100 text-amber-700',
        description: 'The core lesson succeeded, but one or more optional enrichments were omitted.',
    },
    FAILED_AFTER_RETRIES: {
        label: GENERATION_MODE_LABELS.FAILED_AFTER_RETRIES,
        tone: 'danger',
        className: 'bg-rose-100 text-rose-700',
        description: 'Saige could not return a usable lesson after retries and fallback.',
    },
    GENERIC_FALLBACK_REJECTED: {
        label: GENERATION_MODE_LABELS.GENERIC_FALLBACK_REJECTED,
        tone: 'danger',
        className: 'bg-rose-100 text-rose-700',
        description: 'Saige rejected a generic template lesson instead of returning it as a success.',
    },
};

const STATUS_META = {
    present: { label: 'Present', tone: 'success', dot: 'bg-emerald-500' },
    resolved: { label: 'Resolved', tone: 'success', dot: 'bg-emerald-500' },
    used: { label: 'Used', tone: 'success', dot: 'bg-emerald-500' },
    baseline: { label: 'Baseline', tone: 'warning', dot: 'bg-amber-500' },
    not_uploaded: { label: 'Not uploaded', tone: 'neutral', dot: 'bg-slate-300' },
    profile_missing: { label: 'Needs profile', tone: 'warning', dot: 'bg-amber-500' },
    missing: { label: 'Missing', tone: 'neutral', dot: 'bg-slate-300' },
    unavailable: { label: 'Unavailable', tone: 'warning', dot: 'bg-amber-500' },
    defaulting: { label: 'Defaulting', tone: 'warning', dot: 'bg-amber-500' },
    waiting: { label: 'Waiting', tone: 'warning', dot: 'bg-amber-500' },
};

const RELIABILITY_CARD_COPY = {
    fallback_mode: {
        label: 'Baseline fallback',
        detail: 'Successful lessons returned in baseline mode.',
    },
    profile_incomplete: {
        label: 'Profile incomplete',
        detail: 'Attempts blocked because subject or grade was missing.',
    },
    transient_failure: {
        label: 'Transient issues',
        detail: 'Retries or recoverable model interruptions observed.',
    },
    partial_enrichment_omitted: {
        label: 'Partial omissions',
        detail: 'Lessons succeeded with one or more enrichments omitted.',
    },
};

function getStatusMeta(status) {
    return STATUS_META[status] || {
        label: String(status || 'Unknown'),
        tone: 'neutral',
        dot: 'bg-slate-300',
    };
}

function titleCase(value) {
    return String(value || '')
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (match) => match.toUpperCase());
}

function firstNonEmpty(...values) {
    for (const value of values) {
        if (value === null || value === undefined) continue;
        const normalized = String(value).trim();
        if (normalized.length > 0) return normalized;
    }
    return '';
}

function resolveReadinessProfile(readiness, profile = {}) {
    const contextPack = readiness?.context_pack || {};
    const profileContext = readiness?.profile || readiness?.user_profile || {};

    const school = firstNonEmpty(
        profile.school,
        profile.school_name,
        profile.campus,
        profile.campus_name,
        profileContext.school,
        profileContext.school_name,
        profileContext.campus,
        profileContext.campus_name,
        readiness?.school,
        readiness?.school_name,
        readiness?.campus,
        readiness?.campus_name,
        contextPack.school,
        contextPack.school_name,
        contextPack.campus,
        contextPack.campus_name,
    );

    const district = firstNonEmpty(
        profile.district,
        profile.district_name,
        profileContext.district,
        profileContext.district_name,
        readiness?.district,
        readiness?.district_name,
        contextPack.district,
        contextPack.district_name,
    );

    return { school, district };
}

function isBanksSpecificTemplateName(value) {
    return /banks\s*j?h/i.test(String(value || ''));
}

function resolveTemplateDisplayName(template = {}, schoolName = '') {
    const backendName = firstNonEmpty(
        template.display_name,
        template.name,
        template.template_name,
        template.label,
    );

    if (schoolName) {
        return `${schoolName} template`;
    }

    if (backendName && !isBanksSpecificTemplateName(backendName)) {
        return backendName;
    }

    return 'Standard lesson template';
}

function resolveContextPackPresentation(contextPack = {}, schoolName = '', districtName = '') {
    const missingSchool = !schoolName;
    const missingDistrict = !districtName;

    if (missingSchool && missingDistrict) {
        return {
            status: 'profile_missing',
            detail: 'Add your district and school to use school and district context.',
        };
    }

    if (missingDistrict) {
        return {
            status: 'profile_missing',
            detail: 'Add your district to use district context for this lesson.',
        };
    }

    if (missingSchool) {
        return {
            status: 'profile_missing',
            detail: 'Add your school to use school context for this lesson.',
        };
    }

    const status = contextPack.status || 'unavailable';

    if (contextPack.detail) {
        return { status, detail: contextPack.detail };
    }

    if (status === 'resolved') {
        return {
            status,
            detail: `School and district context resolved for ${schoolName}, ${districtName}.`,
        };
    }

    if (status === 'waiting') {
        return {
            status,
            detail: 'Context-pack resolution will begin after required inputs are complete.',
        };
    }

    return {
        status,
        detail: `School and district profile is set for ${schoolName}, ${districtName}, but live context is unavailable. Generation can continue without it.`,
    };
}

export function getGenerationModePresentation(mode) {
    if (!mode) {
        return {
            label: 'Unknown',
            tone: 'neutral',
            className: 'bg-slate-100 text-slate-600',
            description: 'No generation mode was recorded.',
        };
    }
    return MODE_BADGE_MAP[mode] || {
        label: titleCase(mode),
        tone: 'neutral',
        className: 'bg-slate-100 text-slate-600',
        description: 'Generation mode available.',
    };
}

export function buildReadinessItems(readiness, profile = {}) {
    if (!readiness) return [];

    const requiredInputs = readiness.required_inputs || {};
    const scopeSequence = readiness.scope_sequence || {};
    const uploadedDocs = readiness.uploaded_docs || {};
    const template = readiness.template || {};
    const contextPack = readiness.context_pack || {};
    const { school, district } = resolveReadinessProfile(readiness, profile);

    const scopeDetailText = String(scopeSequence.detail || '').toLowerCase();
    const isBaselineScope = (
        scopeSequence.source === 'sample_default'
        || /baseline sample scope/i.test(scopeDetailText)
        || /\bsample scope\b/i.test(scopeDetailText)
        || /\bdefault scope\b/i.test(scopeDetailText)
        || (scopeDetailText !== '' && /\bbaseline\b/i.test(scopeDetailText))
    );

    const uploadedDocCount = Number(uploadedDocs.count || 0);
    const docsPresent = uploadedDocs.status === 'present' || uploadedDocCount > 0;
    const templateDisplayName = resolveTemplateDisplayName(template, school);
    const contextPresentation = resolveContextPackPresentation(contextPack, school, district);

    return [
        {
            key: 'subject',
            label: 'Subject',
            status: requiredInputs.subject || 'missing',
            detail: requiredInputs.subject === 'present'
                ? 'Required input is available.'
                : 'Add a subject before generation.',
        },
        {
            key: 'grade',
            label: 'Grade',
            status: requiredInputs.grade || 'missing',
            detail: requiredInputs.grade === 'present'
                ? 'Required input is available.'
                : 'Add a grade level before generation.',
        },
        {
            key: 'scope_sequence',
            label: 'Scope / sequence',
            status: isBaselineScope
                ? 'baseline'
                : (scopeSequence.status || 'missing'),
            detail: scopeSequence.detail
                || (isBaselineScope
                    ? 'Using baseline scope support because no uploaded or district scope was found.'
                    : scopeSequence.status === 'present'
                        ? 'Scope support is available.'
                        : 'No scope source is available yet.'),
        },
        {
            key: 'uploaded_docs',
            label: 'Curriculum docs',
            status: docsPresent ? 'present' : 'not_uploaded',
            detail: uploadedDocs.detail
                || (docsPresent
                    ? `${uploadedDocCount} curriculum document${uploadedDocCount === 1 ? '' : 's'} available for enrichment.`
                    : 'No curriculum docs are attached. Saige can still generate a baseline lesson from subject, grade, template, and available campus context.'),
        },
        {
            key: 'template',
            label: 'Template',
            status: template.status || 'defaulting',
            detail: template.detail && !isBanksSpecificTemplateName(template.detail)
                ? template.detail
                : (template.status === 'resolved'
                    ? `${templateDisplayName} is ready to use.`
                    : template.status === 'waiting'
                        ? 'Template resolution will begin after required inputs are complete.'
                        : `${templateDisplayName} will be used if no scoped template resolves.`),
        },
        {
            key: 'context_pack',
            label: 'School context',
            status: contextPresentation.status,
            detail: contextPresentation.detail,
        },
    ].map((item) => ({
        ...item,
        meta: getStatusMeta(item.status),
    }));
}

export function getReadinessSummary(readiness, topicText = '', profile = {}) {
    if (!readiness) {
        return {
            title: 'Checking lesson readiness',
            detail: 'Saige is checking required inputs and optional supports.',
            tone: 'neutral',
            canGenerate: false,
        };
    }

    // ── Derive risk booleans from readiness items, not just top-level flags ──
    const items = buildReadinessItems(readiness, profile);

    const docsItem = items.find((i) => i.key === 'uploaded_docs');
    const docsMissing = docsItem
        ? docsItem.status === 'missing'
          || docsItem.status === 'not_uploaded'
          || /no uploaded curriculum was found/i.test(docsItem.detail || '')
          || /no curriculum docs are attached/i.test(docsItem.detail || '')
        : (readiness.uploaded_docs?.status || 'missing') !== 'present';

    const scopeItem = items.find((i) => i.key === 'scope_sequence');
    const scopeDetail = (scopeItem?.detail || '').toLowerCase();
    const scopeSource = readiness.scope_sequence?.source || '';
    const baselineScope = !scopeSource
        || scopeSource === 'sample_default'
        || /baseline sample scope/i.test(scopeDetail)
        || /\bsample scope\b/i.test(scopeDetail)
        || /\bdefault scope\b/i.test(scopeDetail)
        || (scopeDetail !== '' && /\bbaseline\b/i.test(scopeDetail));

    const topicBlank = (topicText || '').trim().length === 0;

    // ── Override: docs missing + baseline scope + no topic → warn but allow ──
    // The backend supports subject+grade-only baseline fallback; warn instead of blocking.
    if (readiness.ready_for_generation && docsMissing && baselineScope && topicBlank) {
        return {
            title: 'Ready for baseline lesson',
            detail: 'No curriculum docs are attached. Add a topic or upload curriculum for a stronger lesson, or generate a baseline lesson from your subject and grade.',
            tone: 'needs_topic',
            canGenerate: true,
        };
    }

    if (readiness.ready_for_generation) {
        const docsPresent = !docsMissing;
        const contextPackPresent = items.find((item) => item.key === 'context_pack')?.status === 'resolved';
        const hasCurriculumContext = docsPresent || contextPackPresent;

        if (hasCurriculumContext) {
            return {
                title: 'Ready to generate',
                detail: 'Required inputs are present and document enrichment is available.',
                tone: 'success',
                canGenerate: true,
            };
        }

        const hasSpecificTopic = !topicBlank;

        if (hasSpecificTopic) {
            return {
                title: 'Ready with teacher-provided topic',
                detail: 'Saige will generate using your topic as the lesson focus.',
                tone: 'success',
                canGenerate: true,
            };
        }

        // Non-baseline scope, no topic — scope is available
        return {
            title: 'Ready with available scope support',
            detail: 'Required inputs are present. Saige can proceed even without uploaded docs.',
            tone: 'success',
            canGenerate: true,
        };
    }

    return {
        title: 'Complete required inputs to generate',
        detail: 'Subject and grade must be present before lesson generation can start.',
        tone: 'warning',
        canGenerate: false,
    };
}

export function buildGenerationReceiptDetails(receipt) {
    if (!receipt) {
        return {
            items: [],
            warnings: [],
            enrichmentIssues: [],
        };
    }

    const inputs = receipt.inputs || {};
    const template = receipt.template || {};
    const contextPack = receipt.context_pack || {};
    const enrichments = receipt.enrichment_stages || {};

    const enrichmentIssues = Object.entries(enrichments)
        .filter(([, status]) => !['used', 'succeeded'].includes(status))
        .map(([stage, status]) => ({
            stage: titleCase(stage),
            status: titleCase(status),
        }));

    return {
        items: [
            {
                key: 'inputs',
                label: 'Inputs used',
                value: `Subject ${inputs.subject || 'missing'} · Grade ${inputs.grade || 'missing'}`,
            },
            {
                key: 'docs',
                label: 'Docs',
                value: inputs.uploaded_docs_materially_used
                    ? 'Materially used'
                    : inputs.uploaded_docs === 'present'
                        ? 'Available but not used'
                        : 'Not uploaded',
            },
            {
                key: 'template',
                label: 'Template',
                value: template.status === 'defaulted' || template.status === 'defaulting'
                    ? 'Defaulted'
                    : template.status === 'used' || template.status === 'resolved'
                        ? 'Used'
                        : titleCase(template.status || 'unknown'),
                detail: template.template_id ? `Template ID: ${template.template_id}` : null,
            },
            {
                key: 'context_pack',
                label: 'Context pack',
                value: contextPack.status === 'used' || contextPack.status === 'resolved'
                    ? 'Used'
                    : contextPack.status === 'missing' || contextPack.status === 'unavailable'
                        ? 'Unavailable'
                        : titleCase(contextPack.status || 'unknown'),
            },
        ],
        warnings: receipt.warnings || [],
        enrichmentIssues,
    };
}

export function buildReliabilityMetricCards(report) {
    const counts = report?.counts || {};
    return Object.entries(RELIABILITY_CARD_COPY).map(([key, copy]) => ({
        key,
        label: copy.label,
        detail: copy.detail,
        value: Number(counts[key] || 0),
    }));
}
